import { Link, navigate } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  RedirectArrayItem,
  flattenRedirectArray,
} from 'src/utils/redirects'

interface RedirectsData {
  redirectArray?: RedirectArrayItem[]
}

const helpfulLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services/' },
  { label: 'Locations', path: '/locations/' },
  { label: 'Insurance', path: '/insurance/' },
  { label: 'Membership', path: '/membership/' },
  { label: 'Why Tia', path: '/why-tia/' },
]

const NotFoundPage = () => {
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(true)
  /* 
    this state is used to prevent the 404 page content shows up 
    before the redirect happens (in case a redirect exists for the page)
  */
  const [shouldShow404, setShouldShow404] = useState(false)

  // this logic is required to force redirects even if the pageFrom does not exist in sanity
  useEffect(() => {
    const checkRedirect = async () => {
      const currentPath = window.location.pathname

      try {
        const response = await fetch('/redirects.json')

        if (!response.ok) {
          throw new Error('Failed to fetch redirects')
        }

        const redirectsData: RedirectsData = await response.json()
        if (!redirectsData?.redirectArray) {
          setIsCheckingRedirect(false)
          setShouldShow404(true)
          return
        }

        const redirects = flattenRedirectArray(redirectsData.redirectArray)
        const matchingRedirect = redirects.find(
          redirect => redirect.originPath === currentPath
        )

        if (matchingRedirect) {
          if (matchingRedirect.isExternal) {
            window.location.href = matchingRedirect.redirectPath
          } else {
            navigate(matchingRedirect.redirectPath, { replace: true })
          }
          return
        }

        setIsCheckingRedirect(false)
        setShouldShow404(true)
      } catch (error) {
        console.error('Error checking redirects:', error)

        setIsCheckingRedirect(false)
        setShouldShow404(true)
      }
    }

    checkRedirect()

    return () => {
      setIsCheckingRedirect(false)
      setShouldShow404(false)
    }
  }, [])

  if (isCheckingRedirect) {
    return (
      <div style={{ display: 'none' }}>
        <Helmet>
          <title>Redirecting...</title>
        </Helmet>
      </div>
    )
  }

  if (shouldShow404) {
    return (
      <>
        <Helmet>
          <title>404 - Page Not Found</title>
          <body className="page-404" />
          <style>{`
            .page-404 .site__main {
              min-height: 70vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          `}</style>
          <meta name="robots" content="noindex" />
        </Helmet>
        <section
          className="container--l cb mxa p1 outer"
          style={{
            paddingTop: 'clamp(1rem, 4vw, 3rem)',
            paddingBottom: 'clamp(3rem, 8vw, 6rem)',
          }}
        >
          <p
            className="mono"
            style={{
              fontSize: '0.95rem',
              letterSpacing: '0.08em',
              margin: '0 0 1rem',
              textTransform: 'uppercase',
            }}
          >
            404 - Page not found
          </p>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              lineHeight: 1.05,
              margin: '0 0 1.5rem',
              maxWidth: '90%',
            }}
          >
            Oops! <br /> We can't seem to find the page you're looking for.
          </h1>
          <p
            style={{
              fontSize: '1.1rem',
              lineHeight: 1.6,
              margin: '0 0 2rem',
              maxWidth: '36rem',
            }}
          >
            The page may have moved, changed, or no longer be available. Here
            are some helpful links instead.
          </p>
          <nav aria-label="Helpful links">
            <ul
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridTemplateRows: 'repeat(3, auto)',
                gap: '0.75rem',
                listStyle: 'none',
                margin: 0,
                maxWidth: '24rem',
                padding: 0,
              }}
            >
              {helpfulLinks.map(link => (
                <li key={link.path}>
                  <Link
                    className="mono"
                    to={link.path}
                    style={{
                      fontSize: '1rem',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {link.label} -{'>'}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </section>
      </>
    )
  }

  return null
}

export default NotFoundPage
